import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import PasswordInput from "../components/PasswordInput";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const PLANS = [
  { id: "plan_associado", label: "Associado Ativo — R$ 39,90", desc: "Adesão via PIX logo após o cadastro. Matriz forçada 5x4 com bonificação do 1º ao 4º nível. Ciclo de 30 dias." },
];

export default function Register() {
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    name: "", cpf: "", phone: "", email: "",
    password: "", confirm_password: "",
    plan: "plan_associado",
    referral_code: sp.get("ref") || "",
    account_type: sp.get("tipo") === "comprador" ? "buyer" : "associate",
  });
  const buyer = form.account_type === "buyer";
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const ref = sp.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [sp]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Senha deve ter no mínimo 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      if (data.account_type === "buyer") {
        toast.success("Conta de Comprador criada! Aproveite a loja de benefícios.");
        navigate("/beneficios");
      } else {
        toast.success("Conta criada! Agora pague a adesão de R$ 39,90 via PIX.");
        navigate("/aguardando-ativacao");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-28 pb-16 max-w-2xl mx-auto px-4">
        <Card className="p-8 gold-border">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Cadastro</div>
          <h1 className="font-display text-3xl font-bold mt-1">Criar minha conta</h1>
          <p className="text-sm text-muted-foreground mt-2">Preencha os dados abaixo para começar.</p>

          <form className="mt-6 space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="name">Nome completo</Label>
              <Input id="name" value={form.name} onChange={set("name")} required data-testid="reg-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cpf">CPF</Label>
                <Input id="cpf" value={form.cpf} onChange={set("cpf")} required placeholder="000.000.000-00" data-testid="reg-cpf" />
              </div>
              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} required placeholder="(11) 99999-9999" data-testid="reg-phone" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="reg-email" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pwd">Senha</Label>
                <PasswordInput id="pwd" value={form.password} onChange={set("password")} required minLength={6} data-testid="reg-password" />
              </div>
              <div>
                <Label htmlFor="cpwd">Confirmar senha</Label>
                <PasswordInput id="cpwd" value={form.confirm_password} onChange={set("confirm_password")} required minLength={6} data-testid="reg-confirm-password" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3" role="radiogroup">
              {[
                { v: "associate", label: PLANS[0].label, desc: PLANS[0].desc, testid: "reg-plan-plan_associado" },
                { v: "buyer", label: "Comprador — Grátis", desc: "Acesso só à loja de benefícios com desconto (pague com Pix ou saldo recebido). Não entra na matriz e não recebe comissões. Vire associado quando quiser.", testid: "reg-plan-buyer" },
              ].map((o) => (
                <button type="button" key={o.v} role="radio" aria-checked={form.account_type === o.v}
                        onClick={() => setForm((f) => ({ ...f, account_type: o.v }))}
                        className={`p-4 rounded-xl text-left border transition-colors ${form.account_type === o.v ? "gold-border bg-amber-500/5" : "border-border opacity-70 hover:opacity-100"}`}
                        data-testid={o.testid}>
                  <div className="font-medium">{o.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{o.desc}</div>
                </button>
              ))}
            </div>

            <div>
              <Label htmlFor="ref">Código de indicação (opcional)</Label>
              <Input id="ref" value={form.referral_code} onChange={set("referral_code")} placeholder="ex.: joao-silva" data-testid="reg-referral" />
            </div>

            <Button type="submit" disabled={loading} className="gold-btn w-full hover:opacity-95" data-testid="reg-submit">
              {loading ? "Criando..." : buyer ? "Criar conta grátis de Comprador" : "Cadastrar e pagar adesão"}
            </Button>

            <p className="text-sm text-center text-muted-foreground">
              Já tem conta? <Link to="/login" className="gold-text font-medium">Entrar</Link>
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
}
